import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, keyframes } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
// hooks
import useResponsive from 'src/hooks/useResponsive';
// universe
import {
  BRAND_LOGO_WHITE,
  UNIVERSE,
  UNIVERSE_FONT
} from 'src/theme/universe/brand';
import { LOW_POWER } from 'src/theme/universe/lowPower';
import LegalLine from './LegalLine';

// ---------------------------------------------------------------------------
// THE BRAND SURFACE (DESIGN.md §2 "Brand moment", §26 BrandBanner).
//
// The product is called Universe and its brand is one gradient — navy
// #01266D into light blue #80B4ED. This surface is where that gradient is
// explained rather than merely applied: the view from orbit, the planet's
// limb burning light blue against the navy night the rail is made of. Sign
// in here and the blue shell on the other side is already understood.
//
// Everything on it is brand: the lock-up, a headline, one line of copy. No
// operational content, no control that commits anything (§2 RULE).
//
// WHY IT IS DRAWN AND NOT A PICTURE. A JPEG of a planet is somebody else's
// photograph at somebody else's colour temperature; this is the four brand
// hexes and nothing else, it is a few kilobytes, it is sharp at any density,
// and it recolours with the tokens if the brand sheet ever moves.
//
// SECOND PASS, 2026-10-08 (the owner: "improve it big times"). The first cut
// was a blue ball under an empty sky: a sphere 1.78 panels wide ate the lower
// half, the copy floated in the middle with nothing under it, and the only
// depth in the scene was the starfield. What changed:
//
//   · the planet is SMALLER (1.46 panels, apex 0.33) — a horizon with a
//     curve, not a wall, and sky enough for something to happen in it;
//   · TWO orbital tracks now, tilted against each other, and a lit MOON
//     riding the outer one where a bare 7px dot used to be;
//   · an AURORA behind the copy, so the headline sits on something and reads
//     white instead of grey, and a VIGNETTE, so the panel has a frame and the
//     eye is pushed back towards the middle;
//   · the copy block is anchored: lock-up at the top, headline and blurb just
//     above centre, a hairline and the group's name at the foot — a column
//     with a beginning and an end rather than three things adrift.
//
// GEOMETRY. Every distance in the scene is a fraction of the panel's WIDTH,
// never its height, so the horizon keeps its curvature on a 1600px-tall
// monitor and on a 700px-tall laptop alike — only how much sky sits above it
// changes.
//
// This is the `auth` variant, built for the sign-in and portal-chooser hero.
// `dashboard` (the greeting banner) and `loading` (the wordmark) are the same
// sky and belong in this file when their pages are migrated (§28 phase 1).
//
// THIRD PASS, 2026-10-10 (the owner: "i don't like design of login page").
// What was wrong was not the panel: it was that the panel stopped at 860px
// and the other thousand pixels of a 1920 window were empty grey with a
// 456px card adrift in them. The `scene` variant is the same sky taking the
// WHOLE window, with the form card laid over it — one composition instead of
// a picture next to a leftover. See `AuthPortalLayout`.
// ---------------------------------------------------------------------------

const { light: LIGHT, mid: MID, night: NIGHT } = UNIVERSE;

// The planet, in panel widths: how wide the sphere is, and how far its apex
// stands above the bottom edge. The rest follows — a sphere 1.46 panels wide
// whose apex is 0.33 panels up has its centre 0.40 panels below the floor, so
// the horizon sags from 0.33 at the middle to 0.12 at the panel's edges: a
// curve you can read as a sphere, over a third of the panel instead of half.
const PLANET = { width: 1.46, apex: 0.33 };
const COMPACT_PLANET = { width: 3.2, apex: 0.13 };

// THE FULL-BLEED SCENE. Panel widths are the right unit for a tall column
// and the wrong one for a window: at 1920 across, `PLANET` would hang a
// 2800px sphere 630px up the screen and there would be no sky left to put
// anything in. These are the same horizon re-proportioned for a landscape
// frame — wide and shallow, the limb meeting the bottom edge about 750px
// either side of centre with a 160px apex, and the whole window above it.
// The apex is deliberately low: a limb is bright along its whole length, and
// across 1920px that is a lot of lit pixels to put under a form.
const SCENE_PLANET = { width: 1.9, apex: 0.085 };
// Portrait: the same curve has to be legible across 400px, so the sphere is
// relatively wider and stands higher off the floor.
const SCENE_PLANET_NARROW = { width: 2.6, apex: 0.26 };

// The orbital tracks, in the same panel widths, sharing the planet's centre
// below the floor. Each half-height has to clear the planet's apex (0.33) or
// the track hides behind the limb: 0.86 and 0.59 both do. They are tilted
// against each other — one ring is a hoop, two crossing rings are an orbit.
const ORBIT = { width: 2.62, height: 1.72, below: 0.3, tilt: -7 };
const ORBIT_INNER = { width: 2.04, height: 1.18, below: 0.26, tilt: 9 };

// The scene's tracks. Flatter and far wider than the panel's, so what crosses
// the window is an arc and not a circle: the outer one sags about 80px from
// its apex over 1920, which is a curve you can read. The inner one passes
// behind the form card, which is what gives the card something to be in
// front of.
const SCENE_ORBIT = {
  width: 2.6,
  height: 1.05,
  below: 0.06,
  tilt: -5,
  // Where the moon rides, as a fraction ALONG the track — see `Moon`. Tuned
  // on the rendered scene: it sits in the open sky left of the card and
  // above the headline, the one quadrant nothing else occupies.
  moonAt: { top: '1.1%', left: '40%' }
};
const SCENE_ORBIT_INNER = { width: 2.1, height: 0.78, below: 0.05, tilt: 7 };

/** Pushes the sphere down until only `apex` of it is left above the floor. */
const planetShift = (p) => ((p.width - p.apex) / p.width) * 100;

// ---------------------------------------------------------------------------
// WHERE THE SPHERE'S COLOURS GO, given how little of it is on screen.
//
// Only the outer slice of the radius is ever visible — `apex / (width/2)` of
// it — and every stop of the body gradient has to live inside that slice or
// the wrong part of the planet is what you see. The panel shows 45% of the
// radius: a dark navy limb with the atmosphere burning at its edge. The
// full-bleed scene shows 12%, and with the same literal stops that slice is
// nothing BUT the atmosphere — a wall of pale blue across the foot of the
// window, which is exactly how it first came out.
//
// So every stop is placed as a fraction of the VISIBLE slice instead, `f`
// running 0 at the horizon line to 1 at the rim. The `f` values below are the
// panel's own stops solved back into that space, so the panel it was tuned on
// renders unchanged and every other geometry gets the same planet.
// ---------------------------------------------------------------------------

/** The fraction of the radius above the floor. */
const visible = (p) => p.apex / (p.width / 2);

/** `f` (0 horizon → 1 rim) as a percentage of the radius, for this geometry. */
const depthOf = (p) => {
  const v = visible(p) * 100;
  return (f) => (100 - v * (1 - f)).toFixed(3);
};

/**
 * The same idea for the SHADING layers, whose positions are percentages of
 * the sphere's bounding box rather than of its radius: how much shorter this
 * geometry's visible slice is than the panel's, so a latitude band placed on
 * the panel lands at the same place on its own limb here.
 */
const PANEL_SLICE = PLANET.apex / PLANET.width;
const shadeScale = (p) => p.apex / p.width / PANEL_SLICE;

/** The halo disc: 3.5% wider than the planet, sharing its centre. */
const haloOf = (p) => ({
  width: p.width * 1.035,
  apex: p.apex + p.width * 0.0175
});

/** Same, for a track: its centre goes `below` the floor with the planet's. */
const orbitShift = (o) => ((o.height / 2 + o.below) / o.height) * 100;

// ---------------------------------------------------------------------------
// THE STARS.
//
// A fixed seed, so this is the same sky on every render, every reload and
// every screenshot: a starfield that reshuffles between builds is a diff
// nobody can review, and a hero that flickers on a re-render is a bug.
// ---------------------------------------------------------------------------

function mulberry32(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * One CSS layer per star. Radii are in px, not percentages, so a star stays a
 * star when the panel widens instead of swelling into a blob.
 */
function starfield(count, seed) {
  const rand = mulberry32(seed);
  const layers = [];
  for (let i = 0; i < count; i += 1) {
    const x = (rand() * 100).toFixed(2);
    // Biased upward: the sky is what is above the horizon, and the planet
    // would eat an even scatter's bottom third anyway.
    const y = (rand() ** 1.5 * 100).toFixed(2);
    const r = (0.55 + rand() ** 2.4 * 1.65).toFixed(2);
    const a = (0.14 + rand() ** 1.7 * 0.76).toFixed(2);
    layers.push(
      `radial-gradient(${r}px ${r}px at ${x}% ${y}%, rgba(226,238,255,${a}), rgba(226,238,255,0) 100%)`
    );
  }
  return layers.join(', ');
}

const STARS = starfield(118, 0x5eed);
const STARS_COMPACT = starfield(26, 0x5eed);
// The scene covers a whole window rather than half of one, and 118 stars
// spread over two million pixels reads as an empty sky.
const STARS_SCENE = starfield(190, 0x5eed);
// Same seed, a third of the sky: one background layer is one more pass over
// every pixel of the panel at paint time, and a television pays for all 118.
const STARS_LITE = starfield(38, 0x5eed);

// The galaxy: one faint diagonal wash, denser than the field around it. It is
// what stops the scatter reading as noise — a sky has structure.
const GALAXY = [
  `linear-gradient(107deg, transparent 33%, ${alpha(LIGHT, 0.05)} 45%, ${alpha(
    '#C9DCFA',
    0.07
  )} 51%, ${alpha(MID, 0.045)} 58%, transparent 70%)`
].join(', ');

// Two nebulae, both in the brand's own blues: the light end high on the
// right, the gradient's midpoint answering low on the left.
const NEBULAE = [
  `radial-gradient(68% 44% at 88% 4%, ${alpha(LIGHT, 0.17)}, transparent 62%)`,
  `radial-gradient(62% 42% at 2% 32%, ${alpha(MID, 0.2)}, transparent 66%)`
].join(', ');

// THE AURORA: one broad, very soft bloom under where the copy sits, in the
// gradient's own two stops. Nothing in the first cut lifted the ground behind
// the headline, so white type sat on near-black and read grey. This is light,
// not a panel — no edge anywhere on it.
const AURORA = [
  `radial-gradient(74% 46% at 16% 44%, ${alpha(MID, 0.3)}, transparent 72%)`,
  `radial-gradient(52% 30% at 6% 30%, ${alpha(LIGHT, 0.12)}, transparent 70%)`
].join(', ');

const GROUND = `linear-gradient(183deg, #050E24 0%, ${NIGHT} 58%, #02081A 100%)`;

// The frame: the corners fall away, so the panel is a window on something
// rather than a rectangle of navy. Last layer, over everything but the copy.
const VIGNETTE = `radial-gradient(118% 92% at 44% 42%, transparent 52%, rgba(0,4,14,.28) 82%, rgba(0,4,14,.52) 100%)`;

// ---------------------------------------------------------------------------
// MOTION. One moment of life, twice a minute (§11): a meteor crosses the
// upper sky and is gone. The sky is otherwise still — a hero that twinkles is
// a screensaver. `prefers-reduced-motion` removes both.
// ---------------------------------------------------------------------------

const meteor = keyframes({
  '0%, 4%': { opacity: 0, transform: 'translate3d(0, 0, 0) scaleX(0.2)' },
  '6%': { opacity: 0.9 },
  '13%': { opacity: 0, transform: 'translate3d(330px, 0, 0) scaleX(1)' },
  '100%': { opacity: 0, transform: 'translate3d(330px, 0, 0) scaleX(1)' }
});

// The limb breathing, as a planet turning under its star would.
const limb = keyframes({
  '0%, 100%': { opacity: 0.55 },
  '50%': { opacity: 1 }
});

Meteor.propTypes = {
  top: PropTypes.string,
  left: PropTypes.string,
  angle: PropTypes.number,
  length: PropTypes.number,
  duration: PropTypes.number,
  delay: PropTypes.number
};

function Meteor({ top, left, angle, length, duration, delay }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top,
        left,
        transform: `rotate(${angle}deg)`,
        transformOrigin: '0 50%',
        pointerEvents: 'none'
      }}
    >
      <Box
        sx={{
          width: length,
          height: 1.5,
          borderRadius: 1,
          transformOrigin: '100% 50%',
          background: `linear-gradient(90deg, transparent, ${alpha(
            '#DCEAFE',
            0.0
          )} 10%, ${alpha('#DCEAFE', 0.85)} 100%)`,
          boxShadow: `0 0 6px ${alpha(LIGHT, 0.7)}`,
          opacity: 0,
          animation: `${meteor} ${duration}s linear ${delay}s infinite`,
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 0
          }
        }}
      />
    </Box>
  );
}

Sparkle.propTypes = { top: PropTypes.string, left: PropTypes.string, size: PropTypes.number };

/**
 * The three stars bright enough to flare: a white core and two hairlines
 * crossing it, all three drawn as gradients on one box.
 *
 * Not as ::before / ::after bars. `height: 1` in an `sx` is ONE HUNDRED PER
 * CENT, not one pixel — MUI reads 0–1 on width and height as a fraction — so
 * the hairlines came out as grey squares.
 */
function Sparkle({ top, left, size = 14 }) {
  const half = size / 2;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top,
        left,
        width: size,
        height: size,
        pointerEvents: 'none',
        backgroundImage: [
          `radial-gradient(${size / 4}px ${size / 4}px at 50% 50%, #FFFFFF, ${alpha(
            LIGHT,
            0.5
          )} 45%, transparent 100%)`,
          `radial-gradient(${half}px 0.6px at 50% 50%, ${alpha('#EAF2FF', 0.8)}, transparent 100%)`,
          `radial-gradient(0.6px ${half}px at 50% 50%, ${alpha('#EAF2FF', 0.8)}, transparent 100%)`
        ].join(', ')
      }}
    />
  );
}

// ---------------------------------------------------------------------------

Planet.propTypes = {
  geometry: PropTypes.object,
  quiet: PropTypes.bool,
  /** Drop the two buffered layers — the blurred rim, the masked stipple. */
  flat: PropTypes.bool
};

/**
 * The sphere. Its centre is below the floor, so what the panel shows is the
 * limb: near-black navy where the copy would sit, the atmosphere burning
 * through the brand's light blue in the last two per cent of the radius.
 *
 * `closest-side` on every radial here is load-bearing: a radial-gradient in a
 * square box defaults to `farthest-corner`, which puts its 100% stop out at
 * the corner — 41% past the circle the border-radius actually draws, so the
 * rim light would never be reached.
 */
function Planet({ geometry, quiet, flat }) {
  const shift = planetShift(geometry);
  const at = depthOf(geometry);
  const halo = haloOf(geometry);
  const atHalo = depthOf(halo);
  // Every vertical percentage of the shading, in the sphere's box space.
  const k = shadeScale(geometry);
  const y = (pct) => `${(pct * k).toFixed(3)}%`;
  const place = {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    aspectRatio: '1',
    borderRadius: '50%',
    pointerEvents: 'none'
  };
  return (
    <>
      {/* THE ATMOSPHERE, as its own disc 3% wider than the planet and behind
          it: transparent until the sphere's own edge, then one band of the
          brand's light blue falling away into the sky. The first cut did this
          with a soft box-shadow on the planet, which fogged the whole limb
          grey instead of drawing an edge. This one breathes — the only thing
          in the sky that does besides the meteors.

          On a television it is left out altogether: `blur` is an offscreen
          buffer, and this disc is 1.5 panel widths across — around 1300px
          square at the panel's widest, redrawn for every frame of the breath.
          The body's own gradient already burns light blue at its edge, so the
          limb still reads; what goes is the glow outside it. */}
      {!flat && (
      <Box
        aria-hidden
        sx={{
          ...place,
          width: `${halo.width * 100}%`,
          transform: `translate(-50%, ${planetShift(halo)}%)`,
          backgroundImage: `radial-gradient(circle closest-side at 50% 50%, transparent ${atHalo(
            0.8725
          )}%, ${alpha(LIGHT, 0.36)} ${atHalo(0.9277)}%, ${alpha(
            LIGHT,
            0.14
          )} ${atHalo(0.966)}%, transparent 100%)`,
          filter: 'blur(6px)',
          ...(!quiet && {
            animation: `${limb} 20s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
          })
        }}
      />
      )}

      <Box
        aria-hidden
        sx={{
          ...place,
          width: `${geometry.width * 100}%`,
          transform: `translate(-50%, ${shift}%)`,
          backgroundImage: [
            // the star is up and to the left: the lit shoulder
            `radial-gradient(26% ${y(18)} at 25% ${y(3)}, ${alpha('#9FC9F6', 0.22)}, transparent 70%)`,
            // and the right shoulder turns away from it
            `radial-gradient(42% ${y(34)} at 92% ${y(10)}, ${alpha('#010610', 0.7)}, transparent 62%)`,
            // two latitude bands, so the sphere is not a plain wash
            `radial-gradient(70% ${y(7)} at 42% ${y(10)}, ${alpha('#CFE4FB', 0.14)}, transparent 72%)`,
            `radial-gradient(86% ${y(6)} at 54% ${y(19)}, ${alpha(LIGHT, 0.09)}, transparent 74%)`,
            // The body, and the atmosphere burning at its edge — every stop
            // inside the slice of the radius this geometry actually shows.
            `radial-gradient(circle closest-side at 50% 50%, #01091A 0%, #01091A ${at(
              0
            )}%, #02153C ${at(0.0709)}%, #052C6B ${at(0.4249)}%, #0C4FA3 ${at(
              0.7124
            )}%, #3079C9 ${at(0.8783)}%, ${LIGHT} ${at(0.9602)}%, #C9E0FA 100%)`
          ].join(', ')
        }}
      >
      {/* The stipple: the Universe globe is drawn as a field of dots, and the
          planet it signs wears the same grain. Masked to the lit shoulder —
          a texture that reaches the dark side turns the sphere into a ball of
          dotted paper. The mask is also a buffer the size of the sphere, and
          at 16% opacity it is the first thing a television can do without. */}
      {!flat && (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          backgroundImage: `radial-gradient(circle, ${alpha('#CFE2FA', 0.55)} 0.8px, transparent 0.9px)`,
          backgroundSize: '13px 13px',
          opacity: 0.16,
          maskImage: `radial-gradient(40% ${y(24)} at 38% ${y(8)}, #000 0%, rgba(0,0,0,.5) 42%, transparent 100%)`,
          WebkitMaskImage: `radial-gradient(40% ${y(24)} at 38% ${y(8)}, #000 0%, rgba(0,0,0,.5) 42%, transparent 100%)`
        }}
      />
      )}
      </Box>
    </>
  );
}

// ---------------------------------------------------------------------------

Moon.propTypes = { size: PropTypes.number, top: PropTypes.string, left: PropTypes.string };

/**
 * The body riding the outer track: lit from the same star as the planet —
 * upper left — and shaded along the opposite limb, with the glow of an
 * atmosphere-less rock. It replaces the 7px dot of the first cut, which read
 * as a stray pixel rather than as something in orbit.
 *
 * `left` is a position ALONG THE TRACK, not a free coordinate: `top` has to be
 * the ellipse's own height at that x, or the moon floats off its orbit. At
 * 66% of the ring box the arc is 2.63% down from its apex, which lands the
 * moon in the open sky to the right of the copy. Its first position (40.5%)
 * put it directly under the blurb, reading as a bullet beside the text.
 */
function Moon({ size = 24, top = '2.63%', left = '66%' }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top,
        left,
        width: size,
        height: size,
        mt: `${-size / 2}px`,
        ml: `${-size / 2}px`,
        borderRadius: '50%',
        backgroundImage: [
          `radial-gradient(42% 38% at 34% 26%, ${alpha('#FFFFFF', 0.5)}, transparent 70%)`,
          `radial-gradient(circle closest-side at 50% 50%, #F4F8FF 0%, #DCE8FA 46%, #9FBCE4 72%, #4E79B6 92%, #2A4D86 100%)`
        ].join(', '),
        boxShadow: `0 0 ${size}px ${size / 3}px ${alpha(
          LIGHT,
          0.3
        )}, inset ${-size / 4}px ${-size / 6}px ${size / 2.4}px ${alpha('#01122E', 0.72)}`
      }}
    />
  );
}

OrbitRing.propTypes = {
  geometry: PropTypes.object,
  moon: PropTypes.bool,
  opacity: PropTypes.number,
  /** False keeps what rides the track and drops the track — see `Sky`. */
  line: PropTypes.bool
};

/** Where the moon sits when the track does not say — the panel's position. */
const MOON_AT = { top: '2.63%', left: '66%' };

/**
 * An orbital track, and — on the outer one — something on it.
 *
 * Drawn BEFORE the planet, so the sphere cuts it: the hairline rises out of
 * the sky, passes behind the limb and is gone — which is what sells the
 * planet as a sphere rather than a painted arc.
 *
 * The moon rides a SECOND box on the same geometry rather than sitting inside
 * the first: the track's fade is a mask, and a mask takes its children with
 * it, so a moon in the ring box would be half-dissolved by the fade that is
 * only meant for the line.
 */
function OrbitRing({ geometry, moon = false, opacity = 0.38, line = true }) {
  const place = {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    width: `${geometry.width * 100}%`,
    aspectRatio: `1 / ${(geometry.height / geometry.width).toFixed(4)}`,
    transform: `translate(-50%, ${orbitShift(geometry)}%) rotate(${geometry.tilt}deg)`,
    borderRadius: '50%',
    pointerEvents: 'none'
  };
  const fade =
    'linear-gradient(100deg, #000 4%, rgba(0,0,0,.55) 44%, transparent 76%)';
  return (
    <>
      {line && (
        <Box
          aria-hidden
          sx={{
            ...place,
            border: `1px solid ${alpha(LIGHT, opacity)}`,
            maskImage: fade,
            WebkitMaskImage: fade
          }}
        />
      )}
      {moon && (
        <Box aria-hidden sx={place}>
          <Moon {...(geometry.moonAt || MOON_AT)} />
        </Box>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

Sky.propTypes = {
  compact: PropTypes.bool,
  /** The full-bleed window scene rather than the half-window panel. */
  scene: PropTypes.bool,
  /** Scene only: a portrait frame, which wants the taller horizon. */
  narrow: PropTypes.bool
};

function Sky({ compact, scene, narrow }) {
  // eslint-disable-next-line no-nested-ternary
  const geometry = compact
    ? COMPACT_PLANET
    : // eslint-disable-next-line no-nested-ternary
    scene
    ? narrow
      ? SCENE_PLANET_NARROW
      : SCENE_PLANET
    : PLANET;
  // Three skies: the phone band, the full scene, and — on a television, where
  // the full scene is tens of megabytes of buffers in a renderer that has a
  // few hundred for everything — the half of it that costs nothing to draw.
  // See `theme/universe/lowPower`.
  const lite = !compact && LOW_POWER;
  const rich = !compact && !LOW_POWER;
  // A portrait scene gets no tracks: an ellipse 2.6 frame widths across is
  // 1000px of near-straight hairline on a 390px phone, which reads as a
  // stray line rather than as an orbit.
  const rings = scene ? !narrow : !compact;
  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: NIGHT,
          backgroundImage: [
            // eslint-disable-next-line no-nested-ternary
            compact
              ? STARS_COMPACT
              : // eslint-disable-next-line no-nested-ternary
              lite
              ? STARS_LITE
              : scene
              ? STARS_SCENE
              : STARS,
            GALAXY,
            NEBULAE,
            ...(compact ? [] : [AURORA]),
            GROUND
          ].join(', ')
        }}
      />
      {!compact &&
        (scene ? (
          // Clear of the copy on the left and of the card on the right: the
          // scene's open sky is the band between them and the top edge.
          <>
            <Sparkle top="16%" left="31%" size={16} />
            <Sparkle top="9%" left="52%" size={12} />
            <Sparkle top="38%" left="45%" size={10} />
          </>
        ) : (
          <>
            <Sparkle top="13%" left="17%" size={16} />
            <Sparkle top="7%" left="72%" size={12} />
            <Sparkle top="31%" left="57%" size={10} />
          </>
        ))}
      {!compact && (
        <>
          {rich && (
            <>
              <Meteor top="9%" left="-6%" angle={26} length={150} duration={27} delay={5} />
              <Meteor top="2%" left="34%" angle={31} length={110} duration={38} delay={19} />
              {rings && (
                <OrbitRing
                  geometry={scene ? SCENE_ORBIT_INNER : ORBIT_INNER}
                  opacity={0.2}
                />
              )}
            </>
          )}
          {/* The moon rides the outer track on every screen; the TRACK is a
              masked box 2.6 panel widths across — the largest buffer in the
              scene — so a television gets the body without the line it
              travels on. A lit rock in an empty sky still reads as a moon. */}
          {rings && (
            <OrbitRing geometry={scene ? SCENE_ORBIT : ORBIT} moon line={rich} />
          )}
        </>
      )}
      <Planet geometry={geometry} quiet={compact || lite} flat={lite} />
      {!compact && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: VIGNETTE
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

Wordmark.propTypes = { height: PropTypes.number, to: PropTypes.string };

function Wordmark({ height = 34, to = '/' }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      aria-label="LingoYou Universe — choose a portal"
      sx={{
        position: 'relative',
        alignSelf: 'flex-start',
        display: 'inline-flex',
        borderRadius: '4px',
        transition: 'opacity .15s ease',
        '&:hover': { opacity: 0.82 },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 2px ${alpha('#FFFFFF', 0.9)}`
        }
      }}
    >
      <Box
        component="img"
        src={BRAND_LOGO_WHITE}
        alt="LingoYou Universe"
        sx={{ height, width: 'auto', maxWidth: 240, display: 'block' }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------

// The frame the scene's copy is written on, and the frame whatever is laid
// OVER the scene has to share or the two will not line up. Exported for
// exactly that reason — `AuthPortalLayout` puts the form card on it.
export const SCENE_FRAME = {
  width: '100%',
  maxWidth: 1680,
  mx: 'auto',
  px: { xs: 2.5, sm: 4, md: 6, lg: 8 }
};

BrandBanner.propTypes = {
  /**
   * `auth` is the half-window panel; `scene` is the same sky taking the whole
   * window, for a page that lays its own content over it. `dashboard` and
   * `loading` land with their pages.
   */
  variant: PropTypes.oneOf(['auth', 'scene']),
  title: PropTypes.string,
  blurb: PropTypes.string,
  /** The phone band: the same sky at 148px, the limb a glow along its floor. */
  compact: PropTypes.bool,
  /** Where the lock-up goes. The portal chooser, from an auth screen. */
  logoTo: PropTypes.string,
  logo: PropTypes.node,
  sx: PropTypes.object
};

export default function BrandBanner({
  variant = 'auth',
  title,
  blurb,
  compact = false,
  logoTo = '/',
  logo,
  sx
}) {
  const mdUp = useResponsive('up', 'md');
  // The headline only when there is a column free beside the content laid
  // over the scene. Below 1200 the copy and a 560px card cannot both have
  // room, and a headline squeezed to 200px is worse than no headline: the
  // wordmark and the sky carry the brand on their own.
  const lgUp = useResponsive('up', 'lg');

  if (variant === 'scene') {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          color: '#FFFFFF',
          ...sx
        }}
      >
        <Sky scene narrow={!mdUp} />

        <Box
          sx={{
            ...SCENE_FRAME,
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            pt: { xs: 3, md: 5, lg: 6 },
            // Deeper at the foot than at the head: the company line is the
            // one thing down there, and the horizon is coming up to meet it.
            pb: { xs: 3, md: 5, lg: 9 },
            // Nothing here is a control except the lock-up, and the card
            // sits on top: the rest of the surface must not eat its clicks.
            pointerEvents: 'none',
            '& a': { pointerEvents: 'auto' }
          }}
        >
          {logo || <Wordmark height={mdUp ? 34 : 26} to={logoTo} />}

          {lgUp && (
            <>
              {/* Unequal on purpose, as on the panel: dead centre puts a
                  headline on the horizon. */}
              <Box sx={{ flex: 0.85 }} />

              {(title || blurb) && (
                <Box sx={{ maxWidth: { lg: 430, xl: 500 } }}>
                  {title && (
                    <Typography
                      component="p"
                      sx={{
                        fontFamily: UNIVERSE_FONT,
                        fontSize: { lg: 44, xl: 52 },
                        fontWeight: 700,
                        lineHeight: 1.08,
                        letterSpacing: '-0.015em',
                        color: '#FFFFFF',
                        textShadow: `0 1px 24px ${alpha('#000B1F', 0.45)}`
                      }}
                    >
                      {title}
                    </Typography>
                  )}
                  {blurb && (
                    <Typography
                      component="p"
                      sx={{
                        mt: 2.5,
                        maxWidth: 360,
                        fontFamily: UNIVERSE_FONT,
                        fontSize: 15,
                        fontWeight: 400,
                        lineHeight: 1.65,
                        color: alpha('#FFFFFF', 0.78)
                      }}
                    >
                      {blurb}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ flex: 1.15 }} />

              <Box>
                <Box
                  aria-hidden
                  sx={{
                    width: 56,
                    height: '1px',
                    mb: 2,
                    backgroundImage: `linear-gradient(90deg, ${alpha(
                      '#FFFFFF',
                      0.55
                    )}, ${alpha('#FFFFFF', 0)})`
                  }}
                />
                {/* The legal line (the owner, 2026-10-11), kept to the copy
                    column: two balanced lines below 1536 so it never runs
                    under the card, one from there up. Below 1200 the layout
                    sets it under the card instead. */}
                <LegalLine sx={{ maxWidth: { lg: 470, xl: 780 } }} />
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (compact) {
    return (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 4,
          minHeight: 148,
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'flex-start',
          ...sx
        }}
      >
        <Sky compact />
        {logo || <Wordmark height={26} to={logoTo} />}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        p: { md: 5, lg: 7 },
        color: '#FFFFFF',
        ...sx
      }}
      data-variant={variant}
    >
      <Sky />

      <Box sx={{ position: 'relative' }}>
        {logo || <Wordmark to={logoTo} />}
      </Box>

      {/* The copy sits just above the middle — on the aurora, clear of the
          limb. The two spacers are deliberately unequal (0.85 / 1.15): dead
          centre puts a headline on the horizon, where it competes with the
          brightest thing in the panel. */}
      <Box sx={{ flex: 0.85 }} />

      {(title || blurb) && (
        <Box sx={{ position: 'relative', maxWidth: { md: 400, lg: 470 } }}>
          {title && (
            <Typography
              component="p"
              sx={{
                fontFamily: UNIVERSE_FONT,
                // `display`, §3: 36 / 44. Legian is the face this role wants
                // and is not licensed as a woff2 yet (§31 DECISION 2), so the
                // UI face carries it at 700 until it is.
                fontSize: { md: 36, lg: 44 },
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.015em',
                color: '#FFFFFF',
                // The type is the one thing in front of the sky; a hair of
                // shadow keeps it white over the aurora's bright patch.
                textShadow: `0 1px 24px ${alpha('#000B1F', 0.45)}`
              }}
            >
              {title}
            </Typography>
          )}
          {blurb && (
            <Typography
              component="p"
              sx={{
                mt: 2.25,
                maxWidth: 330,
                fontFamily: UNIVERSE_FONT,
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.65,
                color: alpha('#FFFFFF', 0.78)
              }}
            >
              {blurb}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1.15 }} />

      {/* The foot of the column: a hairline that stops short, and the name on
          the paperwork. It gives the copy a base — without it the panel is
          three things floating in a void, which is what the first cut was. */}
      <Box sx={{ position: 'relative' }}>
        <Box
          aria-hidden
          sx={{
            width: 56,
            height: '1px',
            mb: 2,
            backgroundImage: `linear-gradient(90deg, ${alpha(
              '#FFFFFF',
              0.55
            )}, ${alpha('#FFFFFF', 0)})`
          }}
        />
        <LegalLine sx={{ maxWidth: { md: 400, lg: 470 } }} />
      </Box>
    </Box>
  );
}
